/**
 * Hand-off format between the public purchase order creator
 * (`/free-purchase-order-creator`) and the workspace importer
 * (`/home/[account]/orders/import-draft`).
 *
 * The creator flattens its working state into this shape and parks it in
 * localStorage; the visitor then signs up, and the importer picks it up on the
 * other side of authentication. Deliberately a plain, flat, versioned record
 * rather than the creator's internal types: it is a serialization boundary, so
 * neither side should be able to break the other by refactoring, and the
 * importer needs no runtime import from the marketing route.
 *
 * Everything read back out is untrusted — it has been sitting in a store the
 * user can edit — so `readPurchaseOrderDraft` re-validates rather than casting.
 */

const DRAFT_KEY = 'supplyautomate:purchase-order-draft:v1';

/** Drafts older than this are ignored: a stale PO is worse than no PO. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Guards against a pathological paste filling the origin's storage quota. */
const MAX_SERIALIZED_BYTES = 256_000;

const MAX_LINES = 200;
const MAX_FIELD_LENGTH = 2_000;

export type PurchaseOrderDraftLine = {
  description: string;
  hsCode: string;
  lineTotal: number;
  quantity: number;
  sku: string;
  unit: string;
  unitPrice: number;
};

export type PurchaseOrderDraft = {
  currency: string;
  incoterm: string;
  lines: PurchaseOrderDraftLine[];
  notes: string;
  orderDate: string;
  paymentTerms: string;
  poNumber: string;
  shipByDate: string;
  supplierEmail: string;
  supplierName: string;
  total: number;
};

type StoredDraft = {
  draft: PurchaseOrderDraft;
  savedAt: string;
  version: 1;
};

export type LoadedPurchaseOrderDraft = {
  draft: PurchaseOrderDraft;
  savedAt: Date;
};

function coerceString(value: unknown) {
  return typeof value === 'string' ? value.slice(0, MAX_FIELD_LENGTH) : '';
}

function coerceNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : 0;
}

function coerceLine(value: unknown): PurchaseOrderDraftLine | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const source = value as Record<string, unknown>;

  return {
    description: coerceString(source.description),
    hsCode: coerceString(source.hsCode),
    lineTotal: coerceNumber(source.lineTotal),
    quantity: coerceNumber(source.quantity),
    sku: coerceString(source.sku),
    unit: coerceString(source.unit),
    unitPrice: coerceNumber(source.unitPrice),
  };
}

function coerceDraft(value: unknown): PurchaseOrderDraft | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const source = value as Record<string, unknown>;

  const lines = Array.isArray(source.lines)
    ? source.lines
        .slice(0, MAX_LINES)
        .map(coerceLine)
        .filter((line): line is PurchaseOrderDraftLine => line !== null)
    : [];

  // A PO with nothing on it is not worth carrying through a signup flow.
  if (lines.length === 0) {
    return null;
  }

  return {
    currency: coerceString(source.currency) || 'USD',
    incoterm: coerceString(source.incoterm),
    lines,
    notes: coerceString(source.notes),
    orderDate: coerceString(source.orderDate),
    paymentTerms: coerceString(source.paymentTerms),
    poNumber: coerceString(source.poNumber),
    shipByDate: coerceString(source.shipByDate),
    supplierEmail: coerceString(source.supplierEmail),
    supplierName: coerceString(source.supplierName),
    total: coerceNumber(source.total),
  };
}

/**
 * Persists a draft for pickup after signup. Returns false rather than throwing
 * when storage is unavailable (Safari private mode, quota, disabled storage) so
 * the caller can fall back to the PDF download it already offers.
 */
export function savePurchaseOrderDraft(draft: PurchaseOrderDraft) {
  if (typeof window === 'undefined') {
    return false;
  }

  const payload: StoredDraft = {
    draft,
    savedAt: new Date().toISOString(),
    version: 1,
  };

  try {
    const serialized = JSON.stringify(payload);

    if (serialized.length > MAX_SERIALIZED_BYTES) {
      return false;
    }

    window.localStorage.setItem(DRAFT_KEY, serialized);

    return true;
  } catch {
    return false;
  }
}

export function readPurchaseOrderDraft(): LoadedPurchaseOrderDraft | null {
  if (typeof window === 'undefined') {
    return null;
  }

  let raw: string | null = null;

  try {
    raw = window.localStorage.getItem(DRAFT_KEY);
  } catch {
    return null;
  }

  if (!raw) {
    return null;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const stored = parsed as Record<string, unknown>;

  if (stored.version !== 1) {
    return null;
  }

  const draft = coerceDraft(stored.draft);

  if (!draft) {
    return null;
  }

  const savedAt = new Date(coerceString(stored.savedAt));

  if (Number.isNaN(savedAt.getTime())) {
    return null;
  }

  if (Date.now() - savedAt.getTime() > MAX_AGE_MS) {
    return null;
  }

  return { draft, savedAt };
}

export function clearPurchaseOrderDraft() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Nothing to do — a draft we cannot clear is capped by MAX_AGE_MS anyway.
  }
}
